import type { AdminAuditResponse } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { actionLabel, riskLevel } from "../audit-format.js";
import { asMoment } from "../format.js";
import { LoadingState, RetryBanner } from "./ExplorerTools.js";

type Entries = AdminAuditResponse["entries"];

export function ActivityTimeline({ targetId }: { targetId: string }) {
  const [entries, setEntries] = useState<Entries | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setEntries(null);
      setError(null);
      api
        .audit({ ...(signal ? { signal } : {}), targetId })
        .then((page) => setEntries(page.entries))
        .catch((caught: unknown) => {
          if (!signal?.aborted) {
            setError(
              caught instanceof ApiError ? caught.message : "Could not load operator activity.",
            );
          }
        });
    },
    [targetId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <section aria-label="Operator activity" className="entity-activity">
      <div className="entity-activity-head">
        <div>
          <strong>Operator activity</strong>
          <span className="label-13 dim">Actions directly targeting this record.</span>
        </div>
        <div className="entity-activity-actions">
          <button className="btn btn-quiet btn-sm" onClick={() => load()} type="button">
            Refresh
          </button>
          <a
            className="text-link label-13"
            href={`?screen=audit&targetId=${encodeURIComponent(targetId)}`}
          >
            Open full audit log →
          </a>
        </div>
      </div>

      {error ? (
        <div className="entity-activity-state">
          <RetryBanner message={error} onRetry={() => load()} />
        </div>
      ) : entries === null ? (
        <div className="entity-activity-state">
          <LoadingState label="Loading operator activity…" />
        </div>
      ) : entries.length === 0 ? (
        <p className="empty">No operator activity directly targets this record.</p>
      ) : (
        <ol className="timeline entity-activity-list">
          {entries.slice(0, 8).map((entry) => {
            const risk = riskLevel(entry.actionType);
            return (
              <li className="event" key={entry.id}>
                <span className="rail">
                  <span className={risk === "High" ? "node node-warn" : "node"} />
                </span>
                <span className="event-body">
                  <span className="entity-activity-title">
                    <strong className="label-14-strong">{actionLabel(entry.actionType)}</strong>
                    <span className={`badge badge-audit-${risk.toLowerCase()}`}>{risk}</span>
                  </span>
                  {entry.justification ? (
                    <span className="label-13">{entry.justification}</span>
                  ) : null}
                  <span className="label-13 faint">
                    {entry.role} · <span>Operator</span> {entry.adminUserId.slice(0, 8)} ·{" "}
                    {asMoment(entry.createdAt)}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
